'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  PlayerContentMagnifier,
  type PlayerMagnifierRegionsBundle,
} from '@/components/accessibility/PlayerContentMagnifier';

/**
 * Loupe de lecture globale.
 *
 * - Monte une instance unique de `PlayerContentMagnifier` au niveau racine.
 * - Par défaut, la loupe agit sur l'élément `#root` (toute l'application).
 * - Les pages avec des régions plus précises (ex. lecteur éducatif) peuvent
 *   publier leurs propres régions via `useRegisterMagnifierRegions`. Le
 *   bundle courant est lu à chaque frame d'animation par la loupe.
 * - Le clone DOM est invalidé sur `scroll`, `resize`, changement d'historique
 *   (popstate / hashchange / pushState / replaceState) pour rester fidèle.
 */

type GlobalMagnifierContextValue = {
  /** Enregistre un getter de bundle. Renvoie une fonction de cleanup. */
  registerRegions: (getBundle: () => PlayerMagnifierRegionsBundle) => () => void;
  /** Force une invalidation du clone (ex. après ouverture d'une modale). */
  bumpRefresh: () => void;
};

const GlobalMagnifierContext = createContext<GlobalMagnifierContextValue | null>(null);

function useGlobalMagnifierContext(): GlobalMagnifierContextValue | null {
  return useContext(GlobalMagnifierContext);
}

/**
 * Hook à utiliser dans une page qui veut fournir des régions personnalisées
 * pour la loupe. Le getter est appelé en permanence ; il doit retourner la
 * valeur la plus à jour du bundle (typiquement le `.current` d'un ref muté
 * pendant le render).
 */
export function useRegisterMagnifierRegions(
  getBundle: () => PlayerMagnifierRegionsBundle,
): void {
  const ctx = useGlobalMagnifierContext();
  const getterRef = useRef(getBundle);
  getterRef.current = getBundle;

  useEffect(() => {
    if (!ctx) return;
    return ctx.registerRegions(() => getterRef.current());
  }, [ctx]);
}

/** Force le re-clonage de la zone sous le curseur (à utiliser après navigation manuelle). */
export function useMagnifierRefresh(): () => void {
  const ctx = useGlobalMagnifierContext();
  return ctx?.bumpRefresh ?? (() => {});
}

function buildFallbackBundle(): PlayerMagnifierRegionsBundle {
  if (typeof document === 'undefined') return { regions: [] };

  // Cible globale = `#root` (plus fidèle que `body` pour le scroll document).
  const root = document.getElementById('root');
  if (!root) return { regions: [] };

  return {
    regions: [
      {
        id: 'root',
        getBounds: () => root,
        getCloneRoot: () => root,
        cloneMode: 'root',
      },
    ],
  };
}

interface GlobalMagnifierProviderProps {
  children: React.ReactNode;
}

export const GlobalMagnifierProvider: React.FC<GlobalMagnifierProviderProps> = ({
  children,
}) => {
  const customGetterRef = useRef<(() => PlayerMagnifierRegionsBundle) | null>(null);
  const fallbackBundleRef = useRef<PlayerMagnifierRegionsBundle>({ regions: [] });
  const refreshCounterRef = useRef(0);
  const [refreshKey, setRefreshKey] = useState('global:0');

  // Construction unique du fallback (root applicatif).
  useEffect(() => {
    fallbackBundleRef.current = buildFallbackBundle();
  }, []);

  const bumpRefresh = useCallback(() => {
    refreshCounterRef.current += 1;
    setRefreshKey(`global:${refreshCounterRef.current}`);
  }, []);

  const registerRegions = useCallback(
    (getBundle: () => PlayerMagnifierRegionsBundle) => {
      customGetterRef.current = getBundle;
      bumpRefresh();
      return () => {
        if (customGetterRef.current === getBundle) {
          customGetterRef.current = null;
          bumpRefresh();
        }
      };
    },
    [bumpRefresh],
  );

  /**
   * Proxy ref consommé par `PlayerContentMagnifier` : à chaque lecture de
   * `.current`, on renvoie soit le bundle custom (s'il existe) soit le
   * fallback global. Aucune allocation à chaque frame quand custom = null.
   */
  const bundleProxyRef = useMemo(() => {
    const target = {};
    return new Proxy(target, {
      get(_t, prop) {
        if (prop === 'current') {
          const custom = customGetterRef.current;
          if (custom) return custom();
          return fallbackBundleRef.current;
        }
        return undefined;
      },
      set() {
        // Lecture seule côté consommateur : les pages doivent passer par registerRegions.
        return true;
      },
    }) as unknown as React.MutableRefObject<PlayerMagnifierRegionsBundle>;
  }, []);

  // Invalidation du clone lors des événements pouvant changer la mise en page.
  useEffect(() => {
    let rafQueued = false;
    const schedule = () => {
      if (rafQueued) return;
      rafQueued = true;
      requestAnimationFrame(() => {
        rafQueued = false;
        bumpRefresh();
      });
    };

    const onPushState = () => schedule();
    const onReplaceState = () => schedule();

    window.addEventListener('scroll', schedule, { passive: true, capture: true });
    document.addEventListener('scroll', schedule, { passive: true, capture: true });
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('popstate', schedule);
    window.addEventListener('hashchange', schedule);

    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = function patchedPushState(...args: Parameters<typeof origPush>) {
      const r = origPush.apply(this, args);
      onPushState();
      return r;
    };
    window.history.replaceState = function patchedReplaceState(
      ...args: Parameters<typeof origReplace>
    ) {
      const r = origReplace.apply(this, args);
      onReplaceState();
      return r;
    };

    return () => {
      window.removeEventListener('scroll', schedule, { capture: true } as EventListenerOptions);
      document.removeEventListener('scroll', schedule, { capture: true } as EventListenerOptions);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('popstate', schedule);
      window.removeEventListener('hashchange', schedule);
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, [bumpRefresh]);

  const ctxValue = useMemo<GlobalMagnifierContextValue>(
    () => ({ registerRegions, bumpRefresh }),
    [registerRegions, bumpRefresh],
  );

  return (
    <GlobalMagnifierContext.Provider value={ctxValue}>
      {children}
      <PlayerContentMagnifier regionsBundleRef={bundleProxyRef} refreshKey={refreshKey} />
    </GlobalMagnifierContext.Provider>
  );
};

export default GlobalMagnifierProvider;
